import React, { useMemo, useState, useCallback } from 'react';
import { AlertTriangle, Loader2, ExternalLink, ArrowUpDown } from 'lucide-react';
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  ComposedChart,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { formatCurrency } from '../lib/utils';
import { formatCompact } from '../lib/chartFormatters';
import {
  monthKeyToLabel,
  generateMonthAxis,
  ForecastBarShape,
  ForecastReferenceLine,
  ForecastCaveat,
} from '../lib/forecastChartUtils';
import { diagnosticsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

// ── KPI Card ─────────────────────────────────────────────────────────

function KpiCard(props) {
  const { label, value, subDetail, valueClassName } = props;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold font-mono ${valueClassName || ''}`}>{value}</div>
        {subDetail && (
          <p className="text-xs text-muted-foreground mt-1">{subDetail}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Stacked chart tooltip (shows Received, Outstanding, and Total) ───

function CashflowStackedTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const total = row._invoiced || 0;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      <p style={{ color: '#10B981' }}>Received: {formatCurrency(row.Received || 0, currency || 'GBP')}</p>
      <p style={{ color: '#F59E0B' }}>Outstanding: {formatCurrency(row.Outstanding || 0, currency || 'GBP')}</p>
      <p className="border-t mt-1 pt-1 font-medium">Invoiced (= BR OS revenue): {formatCurrency(total, currency || 'GBP')}</p>
      {(row._outside || 0) > 0 && (
        <>
          <p className="text-muted-foreground">Invoices on no booking, not revenue: {formatCurrency(row._outside, currency || 'GBP')}</p>
          <p className="text-muted-foreground">All invoices: {formatCurrency(row._allInvoices || 0, currency || 'GBP')}</p>
        </>
      )}
    </div>
  );
}

// ── Warnings banner ──────────────────────────────────────────────────

function CashflowWarnings(props) {
  const { warnings } = props;
  if (!warnings) return null;
  const items = [];
  if (warnings.quarantined_count > 0) {
    items.push(`${warnings.quarantined_count} invoice(s) quarantined (excluded from totals)`);
  }
  if (warnings.missing_due_date_count > 0) {
    items.push(`${warnings.missing_due_date_count} invoice(s) missing due date (excluded from aging and Invoiced series)`);
  }
  if (warnings.fx_missing_count > 0) {
    items.push(`${warnings.fx_missing_count} invoice(s) missing FX rate (amount excluded from GBP totals)`);
  }
  if (warnings.zero_total_count > 0) {
    items.push(`${warnings.zero_total_count} invoice(s) with zero total amount`);
  }
  if (warnings.dso_methodology) {
    items.push(warnings.dso_methodology);
  }
  if (warnings.received_methodology) {
    items.push(warnings.received_methodology);
  }
  if (items.length === 0) return null;
  return (
    <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700">
      <CardContent className="pt-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">Data Quality Warnings</p>
            <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-0.5">
              {items.map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Aging bar colours (green -> amber -> orange -> red -> dark red) ──

const AGING_COLORS = {
  current: '#10B981',
  '1_30': '#F59E0B',
  '31_60': '#F97316',
  '61_90': '#EF4444',
  '90_plus': '#991B1B',
};

// Bucket key -> query param value the backend expects
const BUCKET_KEY_TO_PARAM = {
  current: 'current',
  '1_30': '1-30',
  '31_60': '31-60',
  '61_90': '61-90',
  '90_plus': '90+',
};

// ── Aging Bucket Drill-down Modal ───────────────────────────────────

// Sortable column header (defined outside component to avoid re-creation per render)
function SortableColumnHead(props) {
  const { field, sortField, sortAsc, onToggle, children, className } = props;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-foreground ${className || ''}`}
      onClick={() => onToggle(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field && (
          <ArrowUpDown className="h-3 w-3" />
        )}
      </span>
    </TableHead>
  );
}

function AgingBucketDrilldownModal(props) {
  const { open, onClose, bucketKey, bucketLabel, bucketAmount, bucketCount, dc } = props;
  const [invoices, setInvoices] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [sortField, setSortField] = useState('days_overdue');
  const [sortAsc, setSortAsc] = useState(false);

  // Fetch when opened
  React.useEffect(() => {
    if (!open || !bucketKey) return;
    let cancelled = false;
    const fetchData = async () => {
      setDrillLoading(true);
      setInvoices([]);
      setMeta(null);
      try {
        const params = {
          bucket: BUCKET_KEY_TO_PARAM[bucketKey],
          currency: dc || 'GBP',
        };
        const { data } = await diagnosticsAPI.cashflowAgingBucketContents(params);
        if (cancelled) return;
        setInvoices(data.invoices || []);
        setMeta(data);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err));
      } finally {
        if (!cancelled) setDrillLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [open, bucketKey, dc]);

  const toggleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortAsc((a) => !a);
        return field;
      }
      setSortAsc(field === 'bros_id');
      return field;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!invoices.length) return [];
    return [...invoices].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [invoices, sortField, sortAsc]);

  // Sanity check: compare modal sum vs card cell value.
  // The card sums in GBP then converts once; the drill-down converts
  // per-invoice then sums.  safe_multiply rounds to 2dp per invoice,
  // so N invoices can diverge by up to N*0.005.  Use a relative
  // threshold (0.1% of the larger value) to avoid false positives
  // from rounding while still catching real bugs.
  const modalSum = meta?.total_outstanding_in_display_currency ?? null;
  const sumMismatch = useMemo(() => {
    if (modalSum === null || bucketAmount === undefined) return false;
    const diff = Math.abs(modalSum - bucketAmount);
    const maxVal = Math.max(Math.abs(modalSum), Math.abs(bucketAmount), 1);
    return diff / maxVal > 0.001;
  }, [modalSum, bucketAmount]);

  const sharedHeadProps = { sortField, sortAsc, onToggle: toggleSort };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {bucketLabel} ({meta?.count ?? bucketCount} invoices, {formatCurrency(modalSum ?? bucketAmount, dc)})
          </DialogTitle>
          <DialogDescription>
            Invoices in the {bucketLabel} aging bucket. Click an invoice number to probe it.
          </DialogDescription>
        </DialogHeader>

        {drillLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!drillLoading && sorted.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No invoices in this bucket.</p>
        )}

        {!drillLoading && sorted.length > 0 && (
          <>
            {sumMismatch && (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded p-2 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Sum mismatch: modal total {formatCurrency(modalSum, dc)} vs card cell {formatCurrency(bucketAmount, dc)}.
                This divergence is itself diagnostic.
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableColumnHead field="bros_id" {...sharedHeadProps}>Invoice</SortableColumnHead>
                    <SortableColumnHead field="company" {...sharedHeadProps}>Client</SortableColumnHead>
                    <SortableColumnHead field="issue_date" {...sharedHeadProps}>Issue Date</SortableColumnHead>
                    <SortableColumnHead field="due_date" {...sharedHeadProps}>Due Date</SortableColumnHead>
                    <SortableColumnHead field="days_overdue" className="text-right" {...sharedHeadProps}>Days Overdue</SortableColumnHead>
                    <SortableColumnHead field="amount_outstanding_in_display_currency" className="text-right" {...sharedHeadProps}>Outstanding ({dc})</SortableColumnHead>
                    <SortableColumnHead field="status" {...sharedHeadProps}>Status</SortableColumnHead>
                    <TableHead className="text-right">Bookings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((inv) => (
                    <TableRow key={inv.bros_id}>
                      <TableCell className="font-mono">
                        <a
                          href={`/diagnostics?probe=${encodeURIComponent(inv.bros_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                        >
                          {inv.bros_id}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[160px]" title={inv.company}>
                        {inv.company || '(unknown)'}
                      </TableCell>
                      <TableCell className="text-sm">{inv.issue_date || 'N/A'}</TableCell>
                      <TableCell className="text-sm">{inv.due_date || 'N/A'}</TableCell>
                      <TableCell className="text-right font-mono">{inv.days_overdue}</TableCell>
                      <TableCell className="text-right font-mono" title={`${formatCurrency(inv.amount_outstanding, inv.currency)} ${inv.currency}`}>
                        {formatCurrency(inv.amount_outstanding_in_display_currency, dc)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{inv.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {inv.booking_link_count > 1 ? (
                          <Badge variant="destructive" className="text-xs">{inv.booking_link_count}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">{inv.booking_link_count}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
              <span>{meta?.count ?? sorted.length} invoice(s)</span>
              <span className="font-mono font-medium text-foreground">
                Total outstanding: {formatCurrency(modalSum ?? 0, dc)}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Monthly Invoicing Drill-down Modal ────────────────────────────────

function MonthlyInvoicingDrilldownModal(props) {
  const { open, onClose, monthKey, series, barValue, dc } = props;
  const [invoices, setInvoices] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [sortField, setSortField] = useState('due_date');
  const [sortAsc, setSortAsc] = useState(true);

  React.useEffect(() => {
    if (!open || !monthKey || !series) return;
    let cancelled = false;
    const fetchData = async () => {
      setDrillLoading(true);
      setInvoices([]);
      setMeta(null);
      try {
        const params = {
          month: monthKey,
          series,
          currency: dc || 'GBP',
        };
        const { data } = await diagnosticsAPI.cashflowMonthlyInvoicingContents(params);
        if (cancelled) return;
        setInvoices(data.invoices || []);
        setMeta(data);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err));
      } finally {
        if (!cancelled) setDrillLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [open, monthKey, series, dc]);

  const toggleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortAsc((a) => !a);
        return field;
      }
      setSortAsc(field === 'due_date' || field === 'bros_id');
      return field;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!invoices.length) return [];
    return [...invoices].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [invoices, sortField, sortAsc]);

  const modalSum = meta?.total_in_display_currency ?? null;
  const sumMismatch = useMemo(() => {
    if (modalSum === null || barValue === undefined || barValue === null) return false;
    const diff = Math.abs(modalSum - barValue);
    const maxVal = Math.max(Math.abs(modalSum), Math.abs(barValue), 1);
    return diff / maxVal > 0.001;
  }, [modalSum, barValue]);

  const seriesLabel = series === 'all' ? 'All Invoices' : series === 'invoiced' ? 'Invoiced (by due date)' : 'Received';
  const sharedHeadProps = { sortField, sortAsc, onToggle: toggleSort };
  const modalPaid = meta?.total_paid_in_display_currency ?? null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {monthKey ? `${meta?.month_label || monthKey} ${monthKey.slice(0, 4)}` : ''} — {seriesLabel} ({meta?.count ?? '...'} invoices, {formatCurrency(modalSum ?? barValue ?? 0, dc)})
          </DialogTitle>
          <DialogDescription>
            {series === 'all'
              ? 'All invoices with due date in this month. Click an invoice number to probe it.'
              : series === 'invoiced'
                ? 'Non-paid invoices with due date in this month. Click an invoice number to probe it.'
                : 'Paid invoices attributed to this month by due date. Click an invoice number to probe it.'}
          </DialogDescription>
        </DialogHeader>

        {drillLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!drillLoading && sorted.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No invoices in this bucket.</p>
        )}

        {!drillLoading && sorted.length > 0 && (
          <>
            {sumMismatch && (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 rounded p-2 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Sum mismatch: modal total {formatCurrency(modalSum, dc)} vs chart bar {formatCurrency(barValue, dc)}.
                This divergence is itself diagnostic.
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableColumnHead field="bros_id" {...sharedHeadProps}>Invoice</SortableColumnHead>
                    <SortableColumnHead field="company" {...sharedHeadProps}>Client</SortableColumnHead>
                    <SortableColumnHead field="issue_date" {...sharedHeadProps}>Issue Date</SortableColumnHead>
                    <SortableColumnHead field="due_date" {...sharedHeadProps}>Due Date</SortableColumnHead>
                    <SortableColumnHead field="status" {...sharedHeadProps}>Status</SortableColumnHead>
                    <SortableColumnHead field="total_amount_in_display_currency" className="text-right" {...sharedHeadProps}>Total ({dc})</SortableColumnHead>
                    <SortableColumnHead field="amount_paid_in_display_currency" className="text-right" {...sharedHeadProps}>Paid ({dc})</SortableColumnHead>
                    <TableHead className="text-right">Bookings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((inv) => (
                    <TableRow key={inv.bros_id}>
                      <TableCell className="font-mono">
                        <a
                          href={`/diagnostics?probe=${encodeURIComponent(inv.bros_id)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                        >
                          {inv.bros_id}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </TableCell>
                      <TableCell className="text-sm truncate max-w-[160px]" title={inv.company}>
                        {inv.company || '(unknown)'}
                      </TableCell>
                      <TableCell className="text-sm">{inv.issue_date || 'N/A'}</TableCell>
                      <TableCell className="text-sm">{inv.due_date || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{inv.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono" title={`${formatCurrency(inv.total_amount, inv.currency)} ${inv.currency}`}>
                        {formatCurrency(inv.total_amount_in_display_currency, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(inv.amount_paid_in_display_currency ?? 0, dc)}
                      </TableCell>
                      <TableCell className="text-right">
                        {inv.booking_link_count > 1 ? (
                          <Badge variant="destructive" className="text-xs">{inv.booking_link_count}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">{inv.booking_link_count}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
              <span>{meta?.count ?? sorted.length} invoice(s)</span>
              <span className="font-mono font-medium text-foreground">
                Total: {formatCurrency(modalSum ?? 0, dc)}
                {modalPaid !== null && <span className="text-muted-foreground ml-3">Paid: {formatCurrency(modalPaid, dc)}</span>}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ── Cashflow Tab ─────────────────────────────────────────────────────

// Props destructured in body, not params: workaround for Emergent babel-metadata-plugin crash (line 936). Do not convert to function({...}).
export function BROSDashboardCashflowTab(props) {
  const { data, loading, dc, cx, period } = props;
  // ── Aging drill-down state ──
  const [drillBucket, setDrillBucket] = useState(null);

  // ── Monthly invoicing drill-down state ──
  const [monthlyDrill, setMonthlyDrill] = useState(null);

  // Convert monthly series to display currency
  const gbpToEur = useMemo(() => {
    const eurGbp = data?.fx_rates?.EUR_GBP;
    return eurGbp && eurGbp > 0 ? 1 / eurGbp : null;
  }, [data]);

  // Build chart data with full axis template.
  // Stacked bar: Received (paid portion, bottom) + Outstanding (top).
  // Total height = Invoiced = Received + Outstanding.
  const chartData = useMemo(() => {
    const byMonth = new Map();

    // Pre-fill axis template with zeros (bars render 0 height, not gaps)
    const axis = generateMonthAxis(period);
    if (axis) {
      for (const mk of axis) {
        byMonth.set(mk, {
          month: monthKeyToLabel(mk),
          monthKey: mk,
          Received: 0,
          Outstanding: 0,
          _invoiced: 0,
          _isForecast: false,
        });
      }
    }

    // Overlay actuals with currency conversion
    const convert = dc !== 'GBP' && gbpToEur ? (v) => v * gbpToEur : (v) => v;
    const actualsMonths = new Set();
    for (const m of (data?.months || [])) {
      actualsMonths.add(m.month);
      // Bars = the REVENUE part (invoices on revenue bookings), so the bar
      // equals the P&L / Group BR OS revenue for the month exactly (Fan,
      // 2026-10-15). Cancellation fees are revenue since 2026-10-24; only
      // invoices on no booking ride in the tooltip. Older API payloads without
      // invoiced_revenue fall back to the full figure.
      const hasRevenueSplit = m.invoiced_revenue != null;
      const invoiced = convert(hasRevenueSplit ? m.invoiced_revenue : (m.invoiced || 0));
      const received = convert(hasRevenueSplit ? (m.received_revenue || 0) : (m.received || 0));
      byMonth.set(m.month, {
        month: monthKeyToLabel(m.month),
        monthKey: m.month,
        Received: received,
        Outstanding: Math.max(0, invoiced - received),
        _invoiced: invoiced,
        _outside: hasRevenueSplit ? convert(m.invoiced_outside_volume || 0) : 0,
        _allInvoices: convert(m.invoiced || 0),
        _isForecast: false,
      });
    }

    // Overlay forecast (Outstanding only; Received has no forecast).
    // Defensive guard: skip current and past months to prevent double-counting.
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    for (const m of (data?.forecast_months || [])) {
      if (m.month <= currentMonthKey) {
        console.warn(`[Cashflow] Forecast month ${m.month} <= current month; skipped`);
        continue;
      }
      const fcInvoiced = convert(m.invoiced || 0);
      const existing = byMonth.get(m.month);
      if (existing) {
        existing.Outstanding = (existing.Outstanding || 0) + fcInvoiced;
        existing._invoiced = (existing._invoiced || 0) + fcInvoiced;
        if (!actualsMonths.has(m.month)) {
          existing._isForecast = true;
        }
      } else {
        byMonth.set(m.month, {
          month: monthKeyToLabel(m.month),
          monthKey: m.month,
          Received: 0,
          Outstanding: fcInvoiced,
          _invoiced: fcInvoiced,
          _isForecast: true,
        });
      }
    }

    return Array.from(byMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);
  }, [data, dc, gbpToEur, period]);

  const hasForecast = useMemo(
    () => chartData.some((d) => d._isForecast),
    [chartData]
  );

  // Convert aging buckets to display currency (for bar chart + table)
  const aging = data?.aging || { buckets: [], total: 0 };
  const displayAging = useMemo(() => {
    return {
      buckets: aging.buckets.map((b) => ({
        ...b,
        amount: cx(b.amount),
      })),
      total: cx(aging.total),
    };
  }, [aging, cx]);

  if (!data && loading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading cashflow data...</p>;
  }

  if (!data && !loading) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No cashflow data available.</p>;
  }

  const kpis = data.kpis || {};
  const topOverdue = data.top_overdue_clients || [];

  return (
    <div className="space-y-6">
      {/* ── KPI tiles ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="DSO"
          value={`${kpis.dso_days || 0} days`}
          subDetail="Paid: issue-to-due proxy; unpaid: actual aging"
        />
        <KpiCard
          label={`Outstanding (${dc})`}
          value={formatCurrency(cx(kpis.total_outstanding || 0), dc)}
          subDetail={`${kpis.overdue_count || 0} overdue`}
        />
        <KpiCard
          label={`Overdue (${dc})`}
          value={formatCurrency(cx(kpis.total_overdue || 0), dc)}
          valueClassName={(kpis.total_overdue || 0) > 0 ? 'text-red-600 dark:text-red-400' : ''}
        />
        <KpiCard
          label="Collection Rate"
          value={`${kpis.collection_rate_pct || 0}%`}
          subDetail={`${formatCurrency(cx(kpis.total_collected || 0), dc)} / ${formatCurrency(cx(kpis.total_invoiced || 0), dc)}`}
        />
      </div>

      {/* ── Monthly Invoicing (stacked bar: Received + Outstanding = Invoiced) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Invoicing (by due date, ex VAT)</CardTitle>
          <p className="text-[11px] text-muted-foreground">Ex VAT, so these bars compare directly with booking volume on the BR OS, Entities and Group dashboards. Received = amount_paid net of VAT, bucketed by due date (issue date fallback).</p>
          <ForecastCaveat show={hasForecast} />
          {(data?.warnings?.invoiced_excluded_no_due_date || 0) > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {data.warnings.invoiced_excluded_no_due_date} invoice(s) missing due date, excluded from Invoiced series
            </p>
          )}
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 28, right: 40, left: 0, bottom: 0 }} stackOffset="none">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatCompact(v, dc)} />
                  <Tooltip content={<CashflowStackedTooltip currency={dc} />} />
                  <Legend />
                  <ForecastReferenceLine />
                  <Bar
                    dataKey="Received"
                    name="Received"
                    stackId="invoiced"
                    fill="#10B981"
                    cursor="pointer"
                    onClick={(barData) => {
                      const entry = barData?.payload || barData;
                      if (entry?.monthKey) {
                        setMonthlyDrill({ monthKey: entry.monthKey, series: 'all', barValue: entry._invoiced });
                      }
                    }}
                  >
                    <LabelList
                      dataKey="Received"
                      position="center"
                      content={({ x, y, width, height, value }) => {
                        if (!value || value < 100 || height < 16) return null;
                        return (
                          <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central" fontSize={9} fill="#fff" fontWeight="600">
                            {formatCompact(value, dc)}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                  <Bar
                    dataKey="Outstanding"
                    name="Outstanding"
                    stackId="invoiced"
                    fill="#F59E0B"
                    shape={<ForecastBarShape data={chartData} />}
                    cursor="pointer"
                    onClick={(barData) => {
                      const entry = barData?.payload || barData;
                      if (entry?.monthKey) {
                        setMonthlyDrill({ monthKey: entry.monthKey, series: 'all', barValue: entry._invoiced });
                      }
                    }}
                  >
                    {/* Stacked total label: reads _invoiced from data row by index
                        (Recharts v3 gotcha: dataKey+formatter on the top segment
                        would show only that segment's value, not the stack total) */}
                    <LabelList
                      content={({ x, y, width, index }) => {
                        const row = chartData[index];
                        if (!row) return null;
                        const total = row._invoiced;
                        if (!total || total < 100) return null;
                        return (
                          <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="#78716C">
                            {formatCompact(total, dc)}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground text-center mt-1">Click a bar to see the invoices</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No monthly data for this period.</p>
          )}
        </CardContent>
      </Card>

      {/* ── Aging breakdown ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Aging chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aging Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {displayAging.buckets.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart
                  layout="vertical"
                  data={displayAging.buckets}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatCompact(v, dc)} />
                  <YAxis dataKey="label" type="category" stroke="hsl(var(--muted-foreground))" fontSize={12} width={80} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
                          <p className="font-medium mb-1">{label}</p>
                          <p>Amount: {formatCurrency(payload[0].value, dc)}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                    {displayAging.buckets.map((bucket) => (
                      <Cell key={bucket.key} fill={AGING_COLORS[bucket.key] || '#6B7280'} />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No aging data.</p>
            )}
          </CardContent>
        </Card>

        {/* Aging summary table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aging Summary</CardTitle>
            <p className="text-xs text-muted-foreground">Click a row to see individual invoices</p>
          </CardHeader>
          <CardContent>
            {displayAging.buckets.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bucket</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayAging.buckets.map((bucket) => {
                    const pct = displayAging.total > 0 ? ((bucket.amount / displayAging.total) * 100).toFixed(1) : '0.0';
                    return (
                      <TableRow
                        key={bucket.key}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setDrillBucket(bucket)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-sm shrink-0"
                              style={{ backgroundColor: AGING_COLORS[bucket.key] || '#6B7280' }}
                            />
                            {bucket.label}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(bucket.amount, dc)}</TableCell>
                        <TableCell className="text-right">{bucket.count}</TableCell>
                        <TableCell className="text-right font-mono">{pct}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="font-bold border-t-2">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(displayAging.total, dc)}</TableCell>
                    <TableCell className="text-right">
                      {displayAging.buckets.reduce((sum, b) => sum + b.count, 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No aging data.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Top overdue clients ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Overdue Clients</CardTitle>
        </CardHeader>
        <CardContent>
          {topOverdue.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Overdue</TableHead>
                    <TableHead className="text-right">Invoices</TableHead>
                    <TableHead>Oldest Due</TableHead>
                    <TableHead>Currencies</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topOverdue.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{c.company}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cx(c.outstanding), dc)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                        {formatCurrency(cx(c.overdue), dc)}
                      </TableCell>
                      <TableCell className="text-right">{c.invoice_count}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.oldest_due_date || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(c.currencies || []).map((cur) => (
                            <Badge key={cur} variant="outline" className="text-xs">{cur}</Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No overdue invoices.</p>
          )}
        </CardContent>
      </Card>

      {/* ── Warnings ── */}
      <CashflowWarnings warnings={data?.warnings} />

      {/* ── Aging bucket drill-down modal ── */}
      <AgingBucketDrilldownModal
        open={!!drillBucket}
        onClose={() => setDrillBucket(null)}
        bucketKey={drillBucket?.key}
        bucketLabel={drillBucket?.label}
        bucketAmount={drillBucket?.amount}
        bucketCount={drillBucket?.count}
        dc={dc}
      />

      {/* ── Monthly invoicing drill-down modal ── */}
      <MonthlyInvoicingDrilldownModal
        open={!!monthlyDrill}
        onClose={() => setMonthlyDrill(null)}
        monthKey={monthlyDrill?.monthKey}
        series={monthlyDrill?.series}
        barValue={monthlyDrill?.barValue}
        dc={dc}
      />
    </div>
  );
}
